import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { MutationStatus, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  Progress,
  Tabs,
  TextFieldForm,
  Typography,
} from 'web-library';
import { GenericErrorResponse } from '../../../api-refactored/typings';
import {
  OtpDetailsResponse,
  useGetOtpDetails,
} from '../../../api-refactored/user/useGetOtpDetails';
import {
  OtpGenerateResponse,
  usePostGenerateOtp,
} from '../../../api-refactored/user/usePostGenerateOtp';
import { usePostOtpVerify } from '../../../api-refactored/user/usePostOtpVerify';
import { GET_USER_DETAILS_QUERY_KEY } from '../../../constants/queryKeys';
import { CUSTOMIZED_UNAUTHORIZED_STATUS_CODE } from '../../../api/config/misc';
import { DefaultOtpType } from '../../../api/user/model';
import { useCountDown } from '../../../hooks/timer';
import { LocationState } from '../../../models/Location';
import RouteKey from '../../../routes/key';
import { useAuth } from '../logic/auth';
import OneTimePasswordHelperMessage from './OneTimePasswordHelperMessage';

const { updatePassword } = RouteKey.auth.child;

const useStyles = makeStyles()((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionText: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  errorText: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    color: 'red',
  },
  button: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  input: {
    marginBottom: theme.spacing(1),
    '& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  otpFormContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  tabIcon: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

interface UserOtpDetails {
  email: {
    emailAddress: string;
    resendTime: number;
  };
  sms: {
    contactNumber: string;
    resendTime: number;
  };
  defaultOtpType: DefaultOtpType;
}

export interface OtpFormData {
  otp: string;
}

const useOTPForm = () => {
  const history = useHistory();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const [selectedTfa, setSelectedTfa] = useState(0);
  const [userOTPDetails, setUserOTPDetails] = useState<UserOtpDetails>();
  const [otpCountDown, setOtpCountDown] = useState(0);
  const [internationalSmsOtpFail, setInternationalSmsOtpFail] = useState(false);
  const location = useLocation<LocationState>();
  const { from } = location?.state || { from: { pathname: '/', search: '?', message: null } };
  const resolvePostOtpRedirect = () => {
    const pathname = from?.pathname || '/';
    const search = from?.search && from.search !== '?' ? from.search : '';
    const target = `${pathname}${search}`;
    if (target === `${location.pathname}${location.search}`) {
      return '/';
    }
    return target;
  };
  const { countDown, countDownEnded, reset } = useCountDown(otpCountDown * 1000);
  const otpForm = useForm<OtpFormData>();

  /** generate Otp for the given default otp type
   * */
  const {
    mutateAsync: generateOtp,
    status: otpGenerateMutationStatus,
    successCount,
    reset: otpGenerateMutationReset,
  } = usePostGenerateOtp({
    onSuccess: (res) => onGenerateSuccess(res),
    onError: (err) => onGenerateFailure(err),
  });

  /** get user detail (defaultOTPType)
   * */
  const { data, isSuccess, isLoading } = useGetOtpDetails({
    enabled: userOTPDetails === undefined,
    onSuccess: async (responseData) => {
      if (responseData) {
        setUserOTPDetails(responseData);
        setSelectedTfa(responseData.defaultOtpType === 'EMAIL' ? 0 : 1);
        await generateOtp({
          resend: false,
          otpType: responseData.defaultOtpType,
        });
      }
    },
    onError: (err) => {
      if (
        err &&
        err.response?.status === CUSTOMIZED_UNAUTHORIZED_STATUS_CODE &&
        err.response.data.code === '1205'
      ) {
        history.push('login');
      }
    },
  });

  /** handle success response from OTP generate endpoint
   * */
  const onGenerateSuccess = (res: AxiosResponse<OtpGenerateResponse>) => {
    const userDetails = res.data;
    if (userDetails) {
      if (res.status === 206) {
        setInternationalSmsOtpFail(true);
      } else {
        setInternationalSmsOtpFail(false);
      }
      setUserOTPDetails(userDetails);
    }
  };

  useEffect(() => {
    if (userOTPDetails) {
      setOtpCountDown(
        selectedTfa === 0 ? userOTPDetails.email.resendTime : userOTPDetails.sms.resendTime
      );
      if (countDownEnded) {
        const counter =
          selectedTfa === 0 ? userOTPDetails.email.resendTime : userOTPDetails.sms.resendTime;
        reset(counter * 1000);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [reset, selectedTfa, userOTPDetails]);

  /** handle failure response from OTP generate endpoint
   * */
  const onGenerateFailure = (_err: AxiosError) => {
    // do nothing
  };

  const otpVerifyMutation = usePostOtpVerify({
    onSuccess: (res) => onVerifySuccess(res),
    onError: (err) => onVerifyError(err),
  });

  /** handle success response from OTP verify endpoint
   * */
  const onVerifySuccess = (res: AxiosResponse<any>) => {
    if (res.status === 200) {
      if (res.data === 'Password Expired') {
        // WOGAA | CPEPOKPNEH-2464: set isUserPasswordExpired to true and redirect the user to the Update Password Page
        history.replace(
          `/auth/${updatePassword.route}`,
          location.state
            ? { ...location.state, isUserPasswordExpired: true }
            : { from: { pathname: '/', search: '?' }, isUserPasswordExpired: true }
        );
      } else {
        auth.signin();
        queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
        history.replace(resolvePostOtpRedirect(), {
          from: {
            pathname: location.pathname,
            search: location.search,
          },
        });
      }
    }
  };

  /** handle error response from OTP verify endpoint
   * */
  const onVerifyError = (err: AxiosError<GenericErrorResponse>) => {
    if (
      err?.response?.status === CUSTOMIZED_UNAUTHORIZED_STATUS_CODE &&
      err?.response?.data.code === '1009'
    ) {
      setUserOTPDetails(undefined);
      history.push('/auth/login', {
        from: '/',
        message: err.response.data.message,
        code: err.response.data.code,
      });
    }
  };

  /** handle otp resend
   * */
  const onOtpResend = async (isResend: boolean, selectedOtp: number) => {
    await generateOtp({
      resend: isResend,
      otpType: selectedOtp === 0 ? 'EMAIL' : 'SMS',
    });
  };

  /** to Verify user OTP value
   * */
  const onFormSubmit = (formData: OtpFormData) => {
    otpVerifyMutation.mutate({
      otpType: selectedTfa === 0 ? 'EMAIL' : 'SMS',
      otpValue: formData.otp,
    });
  };

  const onTabChange = async (newValue: number) => {
    otpGenerateMutationReset();
    setSelectedTfa(newValue);
    otpForm.reset();
    setOtpCountDown(0);
    await onOtpResend(false, newValue);
  };

  return {
    selectedTfa,
    userOTPDetails,
    countDown,
    countDownEnded,
    otpForm,
    internationalSmsOtpFail,
    otpGenerateMutationStatus,
    successCount,

    data,
    isSuccess,
    isLoading,

    otpVerifyMutation,
    onOtpResend,
    onFormSubmit,
    onTabChange,
  };
};

const tabValues = (t: TFunction<'translation'>, data: OtpDetailsResponse | undefined) => {
  if (data?.sms === null) {
    return [t('profile->>OneTimePasswordForm.Email')];
  }
  return [t('profile->>OneTimePasswordForm.Email'), t('profile->>OneTimePasswordForm.SMS')];
};

const OneTimePasswordForm = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    selectedTfa,
    userOTPDetails,
    countDown,
    countDownEnded,
    otpForm,
    otpGenerateMutationStatus,
    internationalSmsOtpFail,
    successCount,
    data,
    isSuccess,
    otpVerifyMutation,
    onOtpResend,
    onFormSubmit,
    onTabChange,
    isLoading,
  } = useOTPForm();

  if (isLoading) {
    return (
      <ContentTemplate transparent>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ContentTemplate isDividerBottom>
        <Tabs
          id="otpTabs"
          value={selectedTfa}
          indicatorColor="primary"
          textColor="primary"
          onChange={(event, newValue) => onTabChange(newValue)}
          ariaLabel="otp tabs"
          // @ts-ignore
          tabs={tabValues(t, data)}
        />

        <Box className={classes.otpFormContainer}>
          {isSuccess && data && userOTPDetails && (
            <OneTimePasswordHelperMessage
              otpGenerateMutationStatus={otpGenerateMutationStatus}
              internationalSmsOtpFail={internationalSmsOtpFail}
              emailAddress={userOTPDetails?.email?.emailAddress}
              contactNumber={userOTPDetails?.sms?.contactNumber}
              selectedTfa={selectedTfa}
            />
          )}

          <form onSubmit={otpForm.handleSubmit(onFormSubmit)}>
            <TextFieldForm
              inputRef={(e) =>
                otpForm
                  .register('otp', {
                    required: true,
                    maxLength: 6,
                    minLength: 6,
                  })
                  .ref(e)
              }
              placeholder={t('profile->>OneTimePasswordForm.OTP')}
              name="otp"
              type="number"
              error={!!otpForm.formState.errors.otp || otpVerifyMutation.isError}
              helperText={
                otpVerifyMutation?.error?.response?.data.code === '1401' &&
                t(
                  'profile->>OneTimePasswordForm.You have entered an incorrect/expired One-Time Password. Please try again. {{attemptLeft}} attempt left.',
                  { attemptLeft: otpVerifyMutation?.error?.response?.data?.data?.attemptTime }
                )
              }
              className={classes.input}
              form={otpForm}
            />

            <ErrorInfo
              label={
                (otpForm.formState.errors.otp?.type === 'required' &&
                  ` Please enter 6 digit OTP number.`) ||
                ''
              }
            />
            <ErrorInfo
              label={
                (otpForm.formState.errors.otp?.type === 'maxLength' &&
                  `OTP number must have 6 number.`) ||
                ''
              }
            />

            <ErrorInfo
              label={
                (otpForm.formState.errors.otp?.type === 'minLength' &&
                  `OTP number must have 6 number.`) ||
                ''
              }
            />

            <Box>
              <ApiButton
                id="otp-submit-button"
                status={otpVerifyMutation.status}
                type="submit"
                className={classes.button}
              >
                {t('profile->>OneTimePasswordForm.Submit')}
              </ApiButton>
            </Box>
          </form>
        </Box>
      </ContentTemplate>
      <ContentTemplate continuous>
        <Typography variant="body1">
          {t(
            `profile->>OneTimePasswordForm.If you do not receive an OTP within {{countDown}} , please click on the 'Resend OTP' button here`,
            {
              countDown: countDown.format('mm:ss'),
            }
          )}
        </Typography>

        {successCount > 0 && (
          <ApiButton
            size="small"
            variant="outlined"
            status={successCount > 1 ? otpGenerateMutationStatus : ('idle' as MutationStatus)}
            disabled={!countDownEnded}
            type="button"
            onClick={() => onOtpResend(true, selectedTfa)}
            className={classes.button}
          >
            {t('profile->>OneTimePasswordForm.Resend OTP')}
          </ApiButton>
        )}
      </ContentTemplate>
    </>
  );
};

export default OneTimePasswordForm;
